import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useActiveLocation } from './useActiveLocation'
import { SkyTint } from './SkyTint'
import { LocationPrompt } from './LocationPrompt'
import { HomeEscalation } from '@/features/storm/HomeEscalation'
import { useStormEscalation } from '@/features/storm/useStormEscalation'
import { HourlyStrip } from './HourlyStrip'
import { DailyForecast } from './DailyForecast'
import { PulseTeaser } from '@/features/pulse/PulseTeaser'
import { HomeGlobe } from '@/features/globe/HomeGlobe'
import { SunArcPanel } from '@/features/globe/globe-chrome'
import { ProductsRow } from '@/features/products/ProductsRow'
import { PushNudge } from '@/features/push/PushNudge'
import { Section } from '@/components/Section'
import { SkeletonBlock, ErrorState } from '@/components/feedback'
import {
  usePointQuery,
  useForecastQuery,
  useHourlyQuery,
  useObservationQuery,
} from '@/lib/nws/hooks'
import { tempF } from '@/lib/nws/units'
import { realizedTodayHigh, realizedTodayLow } from '@/lib/nws/today'
import { deriveLevelFromAlerts } from '@/lib/nws/status'
import { OutOfCoverageError } from '@/lib/nws/client'
import { useInView } from '@/components/hooks/useInView'
import './home.css'

export function HomeView() {
  const { saved, locating, error, locate, useDefault } = useActiveLocation()

  const point = usePointQuery(saved)
  const nwsPoint = point.data?.point
  const forecast = useForecastQuery(nwsPoint)
  const hourly = useHourlyQuery(nwsPoint)
  const observation = useObservationQuery(nwsPoint)

  // Severe-weather escalation for the location (live NWS alerts + any ?demo= preview).
  // The AppShell owns the ambient glow / auto-entry; here we render the Home ladder.
  const esc = useStormEscalation()
  const level = deriveLevelFromAlerts(esc.alerts)

  // Pause the below-the-fold strip animations when scrolled out of view.
  const hourlyView = useInView<HTMLDivElement>()
  const dailyView = useInView<HTMLDivElement>()

  // Full-page sun-driven tint — the lowest z-layer behind the whole home page. Portaled
  // to <body>, so it rides along regardless of which branch below renders. The escalation
  // phase transforms it in place: amber on a watch, red on a warning (see SkyTint).
  const sky = <SkyTint lat={saved?.lat ?? null} lon={saved?.lon ?? null} phase={esc.phase} />

  // No location chosen yet → prompt.
  if (!saved) {
    return (
      <>
        {sky}
        <LocationPrompt
          locating={locating}
          error={error}
          onLocate={locate}
          onUseDefault={useDefault}
        />
      </>
    )
  }

  // Prefer the user's chosen/saved label (matches the Locations list) over NWS's
  // nearest-reporting-city label, which can resolve to a neighbouring suburb.
  const label = saved.label ?? point.data?.label

  if (point.error instanceof OutOfCoverageError) {
    return (
      <>
        {sky}
        <ErrorState
          message="That spot is outside NWS coverage (US only). Pick a US location to see conditions."
          onRetry={useDefault}
        />
      </>
    )
  }

  const currentPeriod = hourly.data?.periods[0]
  const currentTemp = tempF(observation.data?.temperature) ?? currentPeriod?.temperature ?? null
  const dailyPeriods = forecast.data?.periods ?? []
  const hourlyPeriods = hourly.data?.periods ?? []
  const todayHi = realizedTodayHigh(dailyPeriods, hourlyPeriods, currentTemp)
  const todayLo = realizedTodayLow(dailyPeriods, hourlyPeriods, currentTemp)

  return (
    <>
      {sky}
      <HomeGlobe
        location={{ lat: saved.lat, lon: saved.lon, label }}
        observation={observation.data ?? null}
        forecast={forecast.data}
        currentPeriod={currentPeriod}
        point={nwsPoint}
        hi={todayHi}
        lo={todayLo}
        alert={
          esc.isLoading ? null : (
            <HomeEscalation
              phase={esc.phase}
              warning={esc.warning}
              watch={esc.watch}
              alerts={esc.alerts}
              level={level}
              locationLabel={label ?? 'your area'}
            />
          )
        }
      />

      <PushNudge locationLabel={label} />

      <Section label="Hourly">
        <div ref={hourlyView.ref} className={hourlyView.inView ? undefined : 'is-paused'}>
          {hourly.isLoading ? (
            <SkeletonBlock height={150} />
          ) : hourly.data ? (
            <HourlyStrip periods={hourly.data.periods} />
          ) : (
            <ErrorState message="Hourly forecast unavailable." onRetry={() => hourly.refetch()} />
          )}
        </div>
      </Section>

      <Section label="7-Day">
        <div ref={dailyView.ref} className={dailyView.inView ? undefined : 'is-paused'}>
          {forecast.isLoading ? (
            <SkeletonBlock height={300} />
          ) : forecast.data ? (
            <DailyForecast
              periods={forecast.data.periods}
              currentTemp={currentTemp}
              todayHigh={todayHi}
              todayLow={todayLo}
            />
          ) : (
            <ErrorState message="Forecast unavailable." onRetry={() => forecast.refetch()} />
          )}
        </div>
      </Section>

      <div className="home-sun">
        <SunArcPanel location={{ lat: saved.lat, lon: saved.lon }} />
      </div>

      <Section
        label="Nationwide"
        action={
          <Link to="/pulse" className="section__action">
            National picture <ChevronRight size={14} />
          </Link>
        }
      >
        <PulseTeaser />
      </Section>

      <Section
        label="Products"
        action={
          <Link to="/products" className="section__action">
            All <ChevronRight size={14} />
          </Link>
        }
      >
        <ProductsRow />
      </Section>
    </>
  )
}
